import {
  makeStyles,
  tokens,
  Field,
  Input,
  Radio,
  Switch,
  Text,
  Card,
  Divider,
} from '@fluentui/react-components';
import {
  LaptopRegular,
  DesktopRegular,
  TvRegular,
  CursorRegular,
  KeyboardRegular,
  BackpackRegular,
} from '@fluentui/react-icons';
import { OnboardingFormData, HardwareConfig } from '../../../hooks/useOnboardingForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  intro: {
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM,
  },
  optionCard: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalL,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  optionCardSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  icon: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    display: 'block',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  peripheralsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  peripheralItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
  },
  peripheralLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  typeSelection: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM,
  },
  typeCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  typeCardSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
});

interface HardwareStepProps {
  formData: OnboardingFormData;
  updateFormData: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  errors: Record<string, string>;
}

export function HardwareStep({ formData, updateFormData, errors }: HardwareStepProps) {
  const styles = useStyles();

  const updateHardware = (updates: Partial<HardwareConfig>) => {
    updateFormData('hardware', { ...formData.hardware, ...updates });
  };

  const handlePCTypeChange = (isReused: boolean) => {
    updateHardware({
      isReused,
      serviceTag: isReused ? formData.hardware.serviceTag : undefined,
      type: isReused ? undefined : formData.hardware.type,
    });
  };

  return (
    <div className={styles.container}>
      <Text className={styles.intro}>
        Specify the hardware requirements for the new employee.
      </Text>

      {/* PC Type Selection */}
      <div>
        <Text style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: tokens.spacingVerticalS, display: 'block' }}>
          PC Type
        </Text>
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalL }}>
          <Card
            className={`${styles.optionCard} ${formData.hardware.isReused ? styles.optionCardSelected : ''}`}
            onClick={() => handlePCTypeChange(true)}
          >
            <div className={styles.icon} style={{ backgroundColor: '#e8f4f8', color: '#006d75' }}>
              <DesktopRegular />
            </div>
            <div className={styles.content}>
              <Text className={styles.title}>Reused PC</Text>
              <Text className={styles.description}>Assign an existing PC to this user</Text>
            </div>
            <Radio checked={formData.hardware.isReused} onChange={() => {}} />
          </Card>

          <Card
            className={`${styles.optionCard} ${!formData.hardware.isReused ? styles.optionCardSelected : ''}`}
            onClick={() => handlePCTypeChange(false)}
          >
            <div className={styles.icon} style={{ backgroundColor: '#e6f4ea', color: '#137333' }}>
              <LaptopRegular />
            </div>
            <div className={styles.content}>
              <Text className={styles.title}>New PC</Text>
              <Text className={styles.description}>Request new hardware for this user</Text>
            </div>
            <Radio checked={!formData.hardware.isReused} onChange={() => {}} />
          </Card>
        </div>
      </div>

      {/* Reused PC - Service Tag */}
      {formData.hardware.isReused && (
        <Field
          label="Service Tag"
          required
          validationMessage={errors.serviceTag}
          validationState={errors.serviceTag ? 'error' : undefined}
        >
          <Input
            value={formData.hardware.serviceTag || ''}
            onChange={(_, data) => updateHardware({ serviceTag: data.value })}
            placeholder="Enter the PC service tag (e.g., ABC123X)"
          />
        </Field>
      )}

      {/* New PC - Type and Peripherals */}
      {!formData.hardware.isReused && (
        <>
          <Field
            label="Device Type"
            required
            validationMessage={errors.hardwareType}
            validationState={errors.hardwareType ? 'error' : undefined}
          >
            <div className={styles.typeSelection}>
              <Card
                className={`${styles.typeCard} ${formData.hardware.type === 'Laptop' ? styles.typeCardSelected : ''}`}
                onClick={() => updateHardware({ type: 'Laptop' })}
              >
                <LaptopRegular style={{ fontSize: '32px', marginBottom: tokens.spacingVerticalS }} />
                <Text style={{ fontWeight: tokens.fontWeightSemibold }}>Laptop</Text>
              </Card>
              <Card
                className={`${styles.typeCard} ${formData.hardware.type === 'Desktop' ? styles.typeCardSelected : ''}`}
                onClick={() => updateHardware({ type: 'Desktop' })}
              >
                <DesktopRegular style={{ fontSize: '32px', marginBottom: tokens.spacingVerticalS }} />
                <Text style={{ fontWeight: tokens.fontWeightSemibold }}>Desktop</Text>
              </Card>
            </div>
          </Field>

          <Divider />

          <div>
            <Text style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: tokens.spacingVerticalS, display: 'block' }}>
              Peripherals
            </Text>
            <div className={styles.peripheralsGrid}>
              <Card className={styles.peripheralItem}>
                <span className={styles.peripheralLabel}>
                  <TvRegular /> Primary Display
                </span>
                <Switch
                  checked={formData.hardware.primaryDisplay}
                  onChange={(_, data) => updateHardware({ primaryDisplay: data.checked })}
                />
              </Card>

              <Card className={styles.peripheralItem}>
                <span className={styles.peripheralLabel}>
                  <TvRegular /> Secondary Display
                </span>
                <Switch
                  checked={formData.hardware.secondaryDisplay}
                  onChange={(_, data) => updateHardware({ secondaryDisplay: data.checked })}
                />
              </Card>

              <Card className={styles.peripheralItem}>
                <span className={styles.peripheralLabel}>
                  <CursorRegular /> Mouse
                </span>
                <Switch
                  checked={formData.hardware.mouse}
                  onChange={(_, data) => updateHardware({ mouse: data.checked })}
                />
              </Card>

              <Card className={styles.peripheralItem}>
                <span className={styles.peripheralLabel}>
                  <KeyboardRegular /> Keyboard
                </span>
                <Switch
                  checked={formData.hardware.keyboard}
                  onChange={(_, data) => updateHardware({ keyboard: data.checked })}
                />
              </Card>

              {formData.hardware.type === 'Laptop' && (
                <Card className={styles.peripheralItem}>
                  <span className={styles.peripheralLabel}>
                    <BackpackRegular /> Laptop Bag
                  </span>
                  <Switch
                    checked={formData.hardware.laptopBag}
                    onChange={(_, data) => updateHardware({ laptopBag: data.checked })}
                  />
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
